import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useNow } from '@/hooks/useNow';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import { useShell } from '@/hooks/useShell';
import { useToast } from '@/hooks/useToast';
import { formatDateLong } from '@/lib/format';
import { DRAWER_ROLE_SWITCH } from '@/lib/navigation';
import { routes } from '@/lib/routes';
import { getUnreadCount, NOTIFICATIONS_CHANGED_EVENT } from '@/lib/notificationsStorage';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcher } from './ThemeSwitcher';

const socials = [
  { id: 'instagram', label: 'IG', href: 'https://instagram.com' },
  { id: 'facebook', label: 'f', href: 'https://facebook.com' },
  { id: 'x', label: 'X', href: 'https://x.com' },
  { id: 'mail', label: '@', href: 'mailto:info@zertteu.academy' },
  { id: 'web', label: 'WWW', href: 'https://zertteu.academy' },
  { id: 'whatsapp', label: 'WA', href: 'https://wa.me/' },
];

const MENU_ICONS = {
  profile: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c.6-5 3.2-8 8-8s7.4 3 8 8" />
    </>
  ),
  notifications: (
    <>
      <path d="M6 9a6 6 0 0 1 12 0v5l2 3H4l2-3V9Z" />
      <path d="M10 20h4" />
    </>
  ),
  favorites: (
    <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z" />
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
    </>
  ),
  logout: <path d="M10 4H5v16h5M14 8l4 4-4 4M18 12H9" />,
} as const;

type MenuAction = keyof typeof MENU_ICONS;

function MenuIcon({ children }: { children: ReactNode }) {
  return (
    <span className="account-menu-ico" aria-hidden="true">
      <svg viewBox="0 0 24 24">{children}</svg>
    </span>
  );
}

export const Topbar = () => {
  const { t, locale } = useI18n();
  const { toggleDrawer, isDrawerOpen, shell } = useShell();
  const { user, logout } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const now = useNow();
  const [isCardOpen, setIsCardOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [unreadNotifs, setUnreadNotifs] = useState(() => getUnreadCount(user?.id));

  useEffect(() => {
    setUnreadNotifs(getUnreadCount(user?.id));
    const handleUpdate = () => {
      setUnreadNotifs(getUnreadCount(user?.id));
    };
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, handleUpdate);
    window.addEventListener('storage', handleUpdate);
    return () => {
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [user?.id]);

  const menuItems: Array<{
    id: MenuAction;
    title: string;
    text: string;
    badge?: string;
  }> = [
    { id: 'profile', title: 'Мой профиль', text: 'Личные и академические данные' },
    {
      id: 'notifications',
      title: 'Уведомления',
      text: 'События и сообщения',
      badge: unreadNotifs > 0 ? String(unreadNotifs) : undefined,
    },
    { id: 'favorites', title: 'Избранные', text: 'Сохранённые записи' },
    { id: 'settings', title: 'Настройки', text: 'Профиль и безопасность' },
  ];

  useOnClickOutside(cardRef, () => setIsCardOpen(false), isCardOpen);

  const roleLabel = (key: string): string => {
    const cabinet = shell?.roles.find((item) => item.key === key);
    if (cabinet) return cabinet.label[locale];
    return DRAWER_ROLE_SWITCH.find((item) => item.code === key)?.label ?? key;
  };

  const handleSignOut = () => {
    logout();
    setIsCardOpen(false);
    notify(t('auth.signedOut'));
    navigate(routes.home);
  };

  const handleMenuAction = (action: MenuAction) => {
    setIsCardOpen(false);
    if (action === 'logout') {
      handleSignOut();
      return;
    }
    if (action === 'profile' && user) {
      navigate(routes.profileSection('account'));
      return;
    }
    if (action === 'settings') {
      navigate(routes.profileSection('settings'));
      return;
    }
    if (action === 'notifications') {
      navigate(routes.notifications, {
        state: { returnTo: `${window.location.pathname}${window.location.search}` || routes.app },
      });
      return;
    }
    if (action === 'favorites') {
      navigate(routes.portalSection('news'));
    }
  };

  return (
    <header className={`topbar${user?.activeRole === 'SYSTEM_OWNER' ? ' topbar--owner' : ''}`}>
      <button
        type="button"
        className="burger"
        data-element="B01-E03.01"
        aria-label={isDrawerOpen ? t('a11y.closeMenu') : t('a11y.openMenu')}
        aria-expanded={isDrawerOpen}
        onClick={toggleDrawer}
      >
        ☰
      </button>

      <LanguageSwitcher />

      <time className="today" data-element="B01-E03.03" dateTime={now.toISOString()}>
        {formatDateLong(now, locale)}
      </time>

      <ThemeSwitcher />

      <nav className="top-icons" data-element="B01-E03.04" aria-label={t('a11y.socials')}>
        {socials.map((social) => (
          <a
            key={social.id}
            className="r51-social-link"
            href={social.href}
            target={social.href.startsWith('mailto:') ? undefined : '_blank'}
            rel={social.href.startsWith('mailto:') ? undefined : 'noreferrer noopener'}
          >
            {social.label}
          </a>
        ))}
      </nav>

      {user ? (
        <div className="account-card account-card--inline" data-element="B01-E03.05" ref={cardRef}>
          <button
            type="button"
            className={`account${isCardOpen ? ' is-open' : ''}`}
            aria-expanded={isCardOpen}
            aria-haspopup="menu"
            onClick={() => setIsCardOpen((open) => !open)}
          >
            {user.avatarUrl ? (
              <img className="av av-img" src={user.avatarUrl} alt="" />
            ) : (
              <i className="av" aria-hidden="true">
                {user.initials}
              </i>
            )}
            <span className="account-meta">
              <strong>{user.fullName}</strong>
              <small>{roleLabel(user.activeRole)}</small>
            </span>
            <b className="caret" aria-hidden="true">
              ⌄
            </b>
          </button>

          <button
            type="button"
            className="account-logout"
            data-element="B01-E03.06"
            onClick={handleSignOut}
          >
            {t('topbar.signOut')}
          </button>

          <div className="account-menu account-menu--template" data-open={isCardOpen} role="menu">
            <div className="account-menu-head">
              <strong>{user.fullName}</strong>
              <small>
                {roleLabel(user.activeRole)}
                {user.academiaId ? ` · ${user.academiaId}` : ''}
              </small>
            </div>

            {menuItems.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className="account-menu-item"
                onClick={() => handleMenuAction(item.id)}
              >
                <MenuIcon>{MENU_ICONS[item.id]}</MenuIcon>
                <span className="account-menu-copy">
                  <strong>{item.title}</strong>
                  <small>{item.text}</small>
                </span>
                <span className={`account-menu-arrow${item.badge ? ' is-badge' : ''}`}>
                  {item.badge ?? '›'}
                </span>
              </button>
            ))}

            <div className="account-menu-sep" />

            <button
              type="button"
              role="menuitem"
              className="account-menu-item is-danger"
              onClick={() => handleMenuAction('logout')}
            >
              <MenuIcon>{MENU_ICONS.logout}</MenuIcon>
              <span className="account-menu-copy">
                <strong>Выйти</strong>
                <small>Завершить сессию</small>
              </span>
              <span className="account-menu-arrow">›</span>
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="account" onClick={() => navigate(routes.login)}>
          <i className="av" aria-hidden="true">
            ZA
          </i>
          <span>{t('topbar.signIn')}</span>
        </button>
      )}
    </header>
  );
};
